/**
 * Notice — an inline banner (web-07's face-photo banner, web-09's
 * "alerts are push-only" note). One line of copy, an optional bold title,
 * an optional link-style action at the end and an optional dismiss.
 *
 *   info     brand-50 ground, a small solid brand "i"   — a tip worth acting on
 *   neutral  page-grey ground, a quiet outline "i"       — context, no action
 *   success  green tag colours                           — something just worked
 *   warning  amber tag colours                           — needs attention
 *   danger   red tag colours, role="alert"               — something failed
 *
 * No emoji: the icon is a real glyph so it renders the same on every phone.
 */
import type { MouseEvent, ReactNode } from "react";
import { IconCheckCircle, IconClose, IconAlertTriangle } from "@/components/common/icons";
import { IconAlertCircle, IconInfo } from "./icons";

export type NoticeTone = "info" | "neutral" | "success" | "warning" | "danger";

const BOX: Record<NoticeTone, string> = {
  info: "bg-brand-50 border-brand-100 text-slate-700",
  neutral: "bg-slate-50 border-slate-200 text-slate-600",
  success: "bg-tag-ok-bg border-transparent text-tag-ok-fg",
  warning: "bg-tag-warn-bg border-transparent text-tag-warn-fg",
  danger: "bg-tag-crit-bg border-transparent text-tag-crit-fg",
};

function ToneIcon({ tone }: { tone: NoticeTone }) {
  if (tone === "info") {
    return (
      <span
        aria-hidden="true"
        className="mt-px w-[18px] h-[18px] rounded-full bg-brand-500 text-white flex items-center justify-center shrink-0"
      >
        <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round">
          <path d="M12 11v6" />
          <path d="M12 7h.01" />
        </svg>
      </span>
    );
  }
  const cls = "w-[18px] h-[18px] shrink-0 mt-px";
  if (tone === "success") return <IconCheckCircle className={cls} />;
  if (tone === "warning") return <IconAlertTriangle className={cls} />;
  if (tone === "danger") return <IconAlertCircle className={cls} />;
  return <IconInfo className={`${cls} text-slate-500`} />;
}

interface Props {
  tone?: NoticeTone;
  title?: ReactNode;
  children?: ReactNode;
  /** A link-style control at the end edge ("Add the photos ›"). */
  action?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  /** Replace the tone's icon (e.g. sparkles for the concept-prompt tip). */
  icon?: ReactNode;
  className?: string;
}

export function Notice({ tone = "info", title, children, action, onDismiss, dismissLabel, icon, className = "" }: Props) {
  return (
    <div
      role={tone === "danger" ? "alert" : undefined}
      className={`flex items-start gap-2.5 rounded-[10px] border px-4 py-3 text-[13px] leading-5 ${BOX[tone]} ${className}`}
    >
      {icon ?? <ToneIcon tone={tone} />}
      {/* On a phone the action drops under the copy instead of squeezing it
          into a narrow column beside it. */}
      <div className="flex-1 min-w-0 flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex-1 min-w-0">
          {title && <p className="font-semibold text-slate-900">{title}</p>}
          {children && <div className={title ? "mt-0.5" : ""}>{children}</div>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={dismissLabel}
          className="-me-1.5 -mt-0.5 w-7 h-7 shrink-0 rounded-md flex items-center justify-center opacity-70 hover:opacity-100 hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <IconClose className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

/** The link-style action a Notice or a table cell carries. */
export function LinkButton({
  children,
  onClick,
  className = "",
  type = "button",
}: {
  children: ReactNode;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  type?: "button" | "submit";
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      className={`inline-flex items-center gap-1 text-[13px] font-semibold text-brand-600 hover:text-brand-700 hover:underline underline-offset-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${className}`}
    >
      {children}
    </button>
  );
}
